import * as React from 'react';
import {
    createStyles,
    Group,
    Text,
    Select as SelectBase,
    SelectProps,
    SelectItem as SelectItemBase
} from '@mantine/core';
import { useTranslation, Trans } from 'react-i18next';

// Components
import { FieldLabelTooltip } from './FieldLabelTooltip';

// Styles
import { getCommonFieldStyles } from '../helpers/fieldStyles';

// Hooks
import { useFieldTranslation } from '../hooks/useFieldTranslation';
import { usePermission } from '../../../hooks/usePermission';

// Types
import { GenericFieldProps } from '../types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: commonFieldStyles.input,
        inputHighlight: commonFieldStyles.inputHighlight,
        labelHighlight: commonFieldStyles.labelHighlight,
        readonlyInput: commonFieldStyles.readonlyInput,
        required: commonFieldStyles.required,
        hiddenDisplay: commonFieldStyles.hiddenDisplay,
        label: commonFieldStyles.label,
        icon: commonFieldStyles.icon,
        error: commonFieldStyles.error,
        wrapper: commonFieldStyles.wrapper,
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        description: commonFieldStyles.description,
        readonlyRightSection: {
            display: 'none'
        },
        rootFullWidth: {
            flex: '1',
            width: '100%'
        }
    };
});

interface ItemProps extends React.ComponentPropsWithoutRef<'div'> {
    label: string;
    description?: string;
    disabled?: boolean;
}

const SelectItem = React.forwardRef<HTMLDivElement, ItemProps>(({ label, description, ...others }: ItemProps, ref) => (
    <div ref={ref} {...others}>
        <Group noWrap>
            <div>
                <Text size="sm">{label}</Text>
                <Text size="xs" opacity={0.65}>
                    {description}
                </Text>
            </div>
        </Group>
    </div>
));

export interface CustomSelectProps extends SelectProps, GenericFieldProps {
    key?: string;
    nothingFoundKey?: string;
    grow?: boolean;
    createValidation?: (query: string) => boolean;
}

export const Select: React.FC<CustomSelectProps> = ({
    readOnly,
    data,
    grow,
    label,
    labelKey,
    labelHasMarkup,
    nothingFoundKey,
    descriptionKey,
    tooltipKey,
    createValidation,
    parentform,
    targetperiod,
    writepermission,
    highlight,
    ...props
}) => {
    const { t } = useTranslation();
    const { classes, cx } = useStyles();
    const selectLabel = useFieldTranslation({
        key: labelKey,
        label,
        formData: parentform?.values,
        targetPeriod: targetperiod,
        hasMarkup: labelHasMarkup
    });
    const descriptionLabel = useFieldTranslation({
        key: descriptionKey,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const nothingFoundLabel = useFieldTranslation({
        key: nothingFoundKey,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const [selectOptions, setSelectOptions] = React.useState(data);
    const hasWritePermission = usePermission(writepermission);

    React.useEffect(
        () => {
            if (
                props.creatable &&
                props.value &&
                !(selectOptions as SelectItemBase[]).some(({ value }) => value === props.value)
            ) {
                const item = { value: props.value, label: props.value } as SelectItemBase;
                setSelectOptions([...data, item]);
            } else {
                setSelectOptions(data);
            }
        },
        props.creatable ? [] : [data]
    );

    const overrideProps = React.useMemo(
        () =>
            hasWritePermission === false
                ? {
                      disabled: true,
                      description: t('notPermitted', { ns: 'common' })
                  }
                : {},
        [hasWritePermission]
    );

    return (
        <SelectBase
            data={selectOptions}
            classNames={{
                root: cx(classes.root, {
                    [classes.rootFullWidth]: grow
                }),
                input: cx(classes.input, {
                    [classes.readonlyInput]: readOnly,
                    [classes.inputHighlight]: typeof highlight === 'string' && highlight === 'true'
                }),
                required: cx(classes.required, {
                    [classes.hiddenDisplay]: readOnly
                }),
                rightSection: cx('', {
                    [classes.readonlyRightSection]: readOnly
                }),
                label: cx(classes.label, {
                    [classes.labelHighlight]: typeof highlight === 'string' && highlight === 'true'
                }),
                icon: cx(classes.icon, {
                    [classes.hiddenDisplay]: readOnly
                }),
                wrapper: cx(classes.wrapper, {
                    [classes.readonlyWrapper]: readOnly
                }),
                error: classes.error,
                description: cx(classes.description, {
                    [classes.hiddenDisplay]: readOnly
                })
            }}
            label={
                <>
                    <span>{selectLabel}</span>
                    {tooltipKey ? <FieldLabelTooltip translationKey={tooltipKey} /> : null}
                </>
            }
            description={descriptionLabel}
            nothingFound={nothingFoundLabel}
            itemComponent={SelectItem}
            getCreateLabel={(query) => t('createLabel', { createdLabel: query, ns: 'common' })}
            shouldCreate={(query: string, data: SelectItemBase[]) => {
                if (!data.length) {
                    return typeof createValidation !== 'undefined' ? createValidation(query) : true;
                } else {
                    return false;
                }
            }}
            onCreate={(query: string) => {
                const item = { value: query, label: query } as SelectItemBase;
                setSelectOptions((current) => [...current, item]);

                return item;
            }}
            withinPortal={true}
            {...props}
            {...overrideProps}
        />
    );
};
